import { useCallback, useContext } from 'react';
import _ from 'underscore';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { handleBulkErrors } from '@bingads-webui-campaign-react/pmax-common';
import { readCampaign, readAssetGroup, readLocationCriterions, createOrUpdateCampaign } from '@bingads-webui-campaign-react/perf-max-data';
import {
  getFallbackEstimates,
  usePreference,
  preferenceNames,
} from '@bingads-webui-campaign-react/pmax-lite-data';
import {
  useCustomerLevelVariables,
  usePostponeReadVariables,
  usePilotsValue,
} from '@bingads-webui-campaign-react/basic-context-utils';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { usePromisify } from '@bingads-webui-react/overreact';
import { useCreateHookResultPromise } from '@bingads-webui-campaign-react/common-hooks';
import { useCurrencies } from '@bingads-webui/overreact-cc';
import { useCurrencyCountryMapping } from '@bingads-webui-campaign-react/use-currency-country-mapping';
import { MAX_CONVERSIONS } from '@bingads-webui-campaign-react/bid-strategy-component';
import { useWizardService, getFlowFactor } from '@bingads-webui-react/wizard';
import { assetGroupModelToViewModel } from '@bingads-webui-campaign-react/pmax-ad-assets';
import { LogTargetCpaCheckOnSave } from '@bingads-webui-campaign-react/target-cpa-warning-banner';

import { useLocalizeBudget } from './utils/use-budget-localized';
import { useBudgetInstrument } from './use-budget-instrument';
import { formatBudgetSuggestionPayload } from './utils/budget-suggestion-helper';
import { formatLocationCriterion } from './utils/budget-suggestion-payload-helper';

export const roasMtToUI = roas => (_.isNumber(roas) ? roas * 100 : null);
export const roasUiToMt = roas => (_.isNumber(roas) ? (roas / 100).toFixed(4) : null);

export const useBudgetRepository = () => {
  const { scope } = useContext(ScopeContext);
  const {
    cid, aid, campaignId, assetGroupId,
  } = scope.values;

  const isBudgetSuggestionV2Enabled = usePilotsValue('isPmaxLiteBudgetSuggestionEnabled');

  const i18n = useContext(I18nContext);

  const budgetActivity = useBudgetInstrument();

  const { state } = useWizardService();
  const isSmartPage = getFlowFactor(state, 'isSmartPage');

  const customerLevelVariables = useCustomerLevelVariables();
  const postponeReadConfig = usePostponeReadVariables();

  const [{ value: countryPreference }] = usePreference(preferenceNames.SelectedCountry);

  const [
    { value: currencyPreference },
    { update: updateCurrencyPreference }]
    = usePreference(preferenceNames.SelectedCurrency);

  const [
    { value: budgetSuggestionTier },
    { update: updateBudgetSuggestionTier }] = usePreference(preferenceNames.BudgetSuggestionTier);

  const {
    getCurrenciesForCountry,
    getDefaultCurrencyForCountry,
    isUseCurrencyCountryMappingReady,
  } = useCurrencyCountryMapping({
    instrumentationActivity: budgetActivity,
    customerLevelVariables,
  });
  const readCurrenciesByCountryPromise = useCreateHookResultPromise({
    result: isUseCurrencyCountryMappingReady && (getCurrenciesForCountry(countryPreference || 'US') || []),
  });

  const readDefaultCurrencyByCountryPromise = useCreateHookResultPromise({
    result: isUseCurrencyCountryMappingReady && (getDefaultCurrencyForCountry(countryPreference || 'US') || []),
  });

  const [, { read: readCurrenciesFn }] = useCurrencies(customerLevelVariables, postponeReadConfig);
  const [readCurrenciesAsync] = usePromisify([readCurrenciesFn]);
  const readCurrencies = useCallback((args = {}, ...rest) => readCurrenciesAsync(args, ...rest), [readCurrenciesAsync]);

  const { localizeBudget } = useLocalizeBudget();

  const loadAssetGroup = useCallback(() => readAssetGroup(cid, aid, campaignId, assetGroupId).then((assetGroup) => {
    budgetActivity.trace(`Load asset group: ${JSON.stringify(assetGroup)}`);

    return assetGroupModelToViewModel(assetGroup);
  }).catch((error) => {
    budgetActivity.error(`Load asset group fail: ${JSON.stringify(error)}`);

    return Promise.reject(error);
  }), [aid, assetGroupId, campaignId, cid, budgetActivity]);

  const extractCampaign = useCallback((campaignData) => {
    const budgetAmount = _.get(campaignData, ['Budget', 'Amount']);
    const bidStrategy = _.get(campaignData, ['BiddingScheme', 'Type']);
    const cpa = _.get(campaignData, ['BiddingScheme', 'TargetCpa']);
    const roas = _.get(campaignData, ['BiddingScheme', 'TargetRoas']);
    const language = _.get(campaignData, ['Languages', 0]);
    const isInitialAdditionalDetailsChecked = _.isNumber(cpa) || _.isNumber(roas);

    return {
      budgetAmount,
      bidStrategy,
      cpa,
      roas: roasMtToUI(roas),
      language,
      isInitialAdditionalDetailsChecked,
    };
  }, []);

  const loadCampaign = useCallback(() => {
    if (!campaignId) {
      return Promise.resolve({
        initialBudget: null,
        bidStrategy: null,
        cpa: null,
      });
    }

    return readCampaign(cid, aid, campaignId).then((campaign) => {
      budgetActivity.trace(`Load campaign succeed: ${JSON.stringify(campaign)}`);

      return campaign;
    }).catch((error) => {
      budgetActivity.error(`Load campaign fail: ${JSON.stringify(error)}`);

      return Promise.reject(error);
    });
  }, [aid, budgetActivity, campaignId, cid]);

  const loadLocationCriterions = useCallback(() => readLocationCriterions(cid, aid, campaignId).then((res) => {
    const criterions = res.value;
    budgetActivity.trace(`read location criterions: ${JSON.stringify(criterions)}`);

    if (_.isEmpty(criterions)) {
      return [];
    }

    return criterions;
  }).catch((error) => {
    budgetActivity.error(`read location criterions fail: ${JSON.stringify(error)}`);

    return Promise.reject(error);
  }), [aid, budgetActivity, campaignId, cid]);

  const loadBudgetSuggestion = useCallback(props => Promise.resolve(getFallbackEstimates(props.currency)), []);

  const load = useCallback(() => {
    const initialRequests = [
      loadCampaign(),
      readCurrencies(),
      readCurrenciesByCountryPromise(),
      readDefaultCurrencyByCountryPromise(),
      loadAssetGroup(),
      loadLocationCriterions(),
    ];

    return Promise.all(initialRequests).then(([
      campaign,
      currenciesData,
      allowedCurrencies,
      defaultCurrency,
      assetGroup,
      criterions,
    ]) => {
      const currency = _.contains(allowedCurrencies, currencyPreference) ? currencyPreference : defaultCurrency;
      const campaignData = extractCampaign(campaign);
      const campaignForSuggestion = {
        ...campaign,
        conversionGoals: {},
        locationTargets: {
          currentLevelSettings: {
            currentLocations: formatLocationCriterion(criterions),
          },
        },
      };

      const budgetSuggestionPayload = formatBudgetSuggestionPayload({ currency });
      const currencies = _.filter(currenciesData, cur => allowedCurrencies.includes(cur.value.Code));

      return loadBudgetSuggestion(budgetSuggestionPayload).then((budgetSuggestion) => {
        const initialBudget = campaignData.budgetAmount || _.get(budgetSuggestion, ['SuggestedBudget', 'DailyBudget']);
        return ({
          ...campaignData,
          currency,
          budgetSuggestion,
          currencies: _.sortBy(currencies, ['value', 'DisplayName']),
          budgetAmount: localizeBudget(initialBudget, currency),
          isBudgetValid: true,
          isMarketingMetricValid: true,
          budgetSuggestionPayload,
          isSmartPage,
          campaignForSuggestion,
          assetGroupForSuggestion: assetGroup,
          budgetTier: budgetSuggestionTier,
          customSaveBudgetPromise: () => Promise.resolve(null),
        });
      });
    }).catch((error) => {
      budgetActivity.error(`Budget step initial requests fail: ${JSON.stringify(error)}`);
      return Promise.reject(error);
    });
  }, [loadCampaign, readCurrencies, readCurrenciesByCountryPromise, readDefaultCurrencyByCountryPromise,
    loadAssetGroup, loadLocationCriterions, currencyPreference, extractCampaign, loadBudgetSuggestion,
    budgetSuggestionTier, localizeBudget, isSmartPage, budgetActivity]);

  const save = ({ data }) => {
    const {
      budgetAmount,
      budgetTier,
      bidStrategy,
      cpa,
      roas,
      currency,
      isBudgetValid,
      isMarketingMetricValid,
      customSaveBudgetPromise,
    } = data || {};

    if (!isBudgetValid || !isMarketingMetricValid) {
      return Promise.reject();
    }

    updateCurrencyPreference(currency);

    if (isBudgetSuggestionV2Enabled) {
      updateBudgetSuggestionTier(budgetTier);
    }

    const updateCampaign = () => {
      let updateBudget = () => Promise.resolve(null);

      const biddingScheme = {
        Type: bidStrategy,
        ...(
          bidStrategy === MAX_CONVERSIONS.type
            ? { TargetCpa: cpa }
            : { TargetRoas: roasUiToMt(roas) }
        ),
      };

      if (isBudgetSuggestionV2Enabled) {
        updateBudget = () => customSaveBudgetPromise(biddingScheme);
      } else {
        const modelToSave = {
          Id: campaignId,
          BiddingScheme: biddingScheme,
          Budget: {
            Amount: i18n.parseDecimal(budgetAmount),
            Type: 'DailyStandard',
          },
        };

        updateBudget = () => createOrUpdateCampaign(modelToSave);
      }

      return updateBudget().then((res) => {
        budgetActivity.trace('Update budget and bid strategy succeed');
        return res;
      }).catch((error) => {
        budgetActivity.error(`Update budget and bid strategy fails: ${JSON.stringify(error)}`);
        return Promise.reject(error);
      });
    };

    return updateCampaign().then(() => {
      budgetActivity.trace('Save budget step succeed');
      LogTargetCpaCheckOnSave(i18n, { dynamic: { TargetCpaValueCheck: true } }, budgetAmount, cpa, budgetActivity, 'pmax-budget-form');
      return data;
    }).catch((error) => {
      budgetActivity.error(`Save budget step fails: ${JSON.stringify(error)}`);
      handleBulkErrors(error, i18n);
      return Promise.reject(error);
    });
  };

  return {
    load,
    save,
  };
};
